import React from 'react';
import { Link } from 'react-router-dom';
import { XCircle, AlertCircle, Clock, Package, CreditCard } from 'lucide-react';

export default function Refund() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-[#102318] via-[#183326] to-[#0b1710] text-white pt-20 px-4 pb-16 font-[Cormorant_Garamond]">
      <section className="max-w-5xl mx-auto">
        <p className="text-[#ffcc32] font-black uppercase text-sm tracking-widest">Policies</p>
        <h1 className="text-5xl md:text-7xl uppercase mt-2 mb-8" style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}>
          Refund & Cancellation
        </h1>
        
        <div className="bg-black/30 border border-white/10 rounded-lg p-8 mb-8">
          <p className="text-zinc-400 leading-relaxed">
            Please read our refund and cancellation policy carefully before placing an order. This policy outlines the conditions under which orders can be cancelled and refunds can be processed.
          </p>
        </div>

        <div className="space-y-8 text-zinc-300">
          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Order Cancellation</h2>
            </div>
            <p className="leading-relaxed mb-4">
              Customers may cancel an order <span className="text-white font-semibold">only before it has been confirmed</span> by our admin team. Once an order has been confirmed or shipped, cancellation is not allowed.
            </p>
            <div className="bg-[#ffcc32]/10 border border-[#ffcc32]/20 rounded-lg p-4 mt-4">
              <p className="text-sm text-zinc-300">
                <span className="text-[#ffcc32] font-semibold">Important:</span> To cancel an order, please contact our support team immediately at <span className="text-white">rridemods@gmail.com</span> or call <span className="text-white">+91 8793678780</span>.
              </p>
            </div>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Refund Processing Time</h2>
            </div>
            <p className="leading-relaxed">
              Refunds (where applicable) are processed within <span className="text-white font-semibold">7 business days</span> back to the original payment method through Razorpay. The actual time for the refund to reflect in your account may vary depending on your bank's processing time.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Package className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Non-Refundable Products</h2>
            </div>
            <p className="leading-relaxed mb-4">
              The following products are <span className="text-white font-semibold">non-refundable</span>:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Customized products</li>
              <li>Products that have been used or damaged by the customer</li>
              <li>Products with opened packaging or missing seals</li>
              <li>Products without original packaging</li>
            </ul>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Return Policy</h2>
            </div>
            <p className="leading-relaxed mb-4">
              Currently, RRide Moto Studio does not provide a general return policy unless a product is received <span className="text-white font-semibold">damaged or incorrect</span>.
            </p>
            <div className="bg-[#ffcc32]/10 border border-[#ffcc32]/20 rounded-lg p-4 mt-4">
              <p className="text-sm text-zinc-300">
                <span className="text-[#ffcc32] font-semibold">Damaged or Incorrect Products:</span> If you receive a damaged or incorrect product, you must contact our support team immediately with clear photographs of the product and packaging for review. Our team will assess the situation and provide appropriate resolution.
              </p>
            </div>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Return Shipping Costs</h2>
            </div>
            <p className="leading-relaxed">
              Customers are responsible for any applicable return shipping costs, unless the return is due to a mistake on our part (e.g., wrong product delivered, damaged product). In such cases, we will provide a prepaid shipping label or reimburse the shipping costs.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Refund Process</h2>
            <p className="leading-relaxed mb-4">
              If your refund request is approved, the following process will be followed:
            </p>
            <ol className="list-decimal list-inside space-y-3 ml-4">
              <li>Our team will verify the product condition and your claim</li>
              <li>Upon approval, a refund initiation request will be sent to Razorpay</li>
              <li>The refund will be processed to your original payment method</li>
              <li>You will receive a confirmation email once the refund is initiated</li>
              <li>The amount will reflect in your account within 5-7 business days (bank dependent)</li>
            </ol>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Partial Refunds</h2>
            <p className="leading-relaxed">
              In certain situations, partial refunds may be granted:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
              <li>Product is not in its original condition, is damaged, or missing parts for reasons not due to our error</li>
              <li>Product is returned more than 7 days after delivery</li>
            </ul>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Exchanges</h2>
            <p className="leading-relaxed">
              We currently do not offer direct exchanges. If you need a different product, you will need to place a new order and return the original product (if eligible for return) following our return policy.
            </p>
          </div>
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#ffcc32]/10 to-transparent border border-[#ffcc32]/20 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white uppercase mb-3">Need Help With a Refund?</h3>
          <p className="text-zinc-400 mb-4">
            If you have any questions about our refund policy or need to request a refund, please contact us:
          </p>
          <div className="space-y-2 text-sm">
            <p><span className="text-[#ffcc32]">Email:</span> rridemods@gmail.com</p>
            <p><span className="text-[#ffcc32]">Phone:</span> +91 8793678780</p>
            <p><span className="text-[#ffcc32]">WhatsApp:</span> +91 8793678780</p>
          </div>
        </div>

        <div className="mt-8 text-center text-zinc-500 text-sm">
          <p>Last Updated: July 2026</p>
        </div>
      </section>
    </main>
  );
}